import { useTranslate } from '@refinedev/core';
import type { CrudFilters } from '@refinedev/core';
import { DatePicker, Flex, Segmented, Spin, Typography, theme } from 'antd';
import dayjs from 'dayjs';
import { useDistributionPeriodCount } from '@/hooks/content_catalog/useDistributionPeriodCount';
import type { DateDimension, DateSelection, Window } from './summaryQueries';
import { WINDOWS, dateWindow } from './summaryQueries';

const DAY = 'YYYY-MM-DD';

/** Live count for one preset — the same `total` trick the alert bar uses. */
const PresetLabel = ({
  label,
  filters,
}: {
  label: string;
  filters: CrudFilters;
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { total, isLoading, isError } = useDistributionPeriodCount(filters);
  return (
    <span style={{ display: 'inline-flex', gap: token.marginXXS }}>
      {label}
      {isLoading ? (
        <Spin size="small" />
      ) : isError ? (
        <Typography.Text type="danger" title={t('common.errors.load_failed')}>
          !
        </Typography.Text>
      ) : (
        <Typography.Text type="secondary">{total ?? '–'}</Typography.Text>
      )}
    </span>
  );
};

interface DateWindowPickerProps {
  /** Which date the presets count against. */
  dimension: DateDimension;
  /** Page-level scope (publisher) applied to every count. */
  scope?: CrudFilters;
  value: DateSelection;
  onChange: (selection: DateSelection) => void;
}

/**
 * Window for a date view: a preset (today / 3 d / 7 d / since Saturday),
 * each with its count, or an explicit range — pick the same day twice for
 * a single date.
 */
export const DateWindowPicker = ({
  dimension,
  scope = [],
  value,
  onChange,
}: DateWindowPickerProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const k = (key: string) =>
    t(`content_catalog.distribution_periods.windows.${key}`);

  return (
    <Flex gap={token.marginSM} align="center" wrap>
      <Segmented<Window | 'range'>
        value={value.kind === 'window' ? value.window : 'range'}
        options={[
          ...WINDOWS.map((w) => ({
            value: w,
            label: (
              <PresetLabel
                label={k(w)}
                filters={[...scope, ...dateWindow(dimension, w, dayjs())]}
              />
            ),
          })),
          { value: 'range', label: k('range') },
        ]}
        onChange={(v) =>
          onChange(
            v === 'range'
              ? {
                  kind: 'range',
                  from: dayjs().format(DAY),
                  to: dayjs().format(DAY),
                }
              : { kind: 'window', window: v }
          )
        }
      />
      {value.kind === 'range' && (
        <DatePicker.RangePicker
          allowClear={false}
          value={[dayjs(value.from), dayjs(value.to)]}
          onChange={(range) => {
            if (!range?.[0] || !range?.[1]) return;
            onChange({
              kind: 'range',
              from: range[0].format(DAY),
              to: range[1].format(DAY),
            });
          }}
        />
      )}
    </Flex>
  );
};
